import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FeedbackAction } from './InlineAlert';
import type { Action, WithClassName } from './types';

export type PageHeaderProps = WithClassName & {
  title: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  breadcrumbs?: readonly { label: string; href?: string }[];
  status?: ReactNode;
  actions?: readonly Action[];
  back?: { label: string; href: string };
  /** Keep the back path available while the title's data is loading. */
  loading?: boolean;
};

function PageLink({ href, children }: { href: string; children: ReactNode }) {
  const absolute = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
  if (!absolute) return <Link to={href}>{children}</Link>;
  const url = new URL(href, window.location.href);
  if (url.origin === window.location.origin && /^https?:$/.test(url.protocol)) {
    return <Link to={`${url.pathname}${url.search}${url.hash}`}>{children}</Link>;
  }
  return <a href={href}>{children}</a>;
}

export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader({
  title, subtitle, icon, breadcrumbs, status, actions, back, loading = false, className,
}, ref) {
  return <header ref={ref} className={`page-header ui-page-header ${className ?? ''}`}>
    {!!breadcrumbs?.length && <nav className="ui-page-header__breadcrumbs" aria-label="Breadcrumb">
      <ol role="list">{breadcrumbs.map((item, index) => <li key={`${item.label}-${item.href ?? ''}`}
        aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}>
        {item.href && index < breadcrumbs.length - 1
          ? <PageLink href={item.href}>{item.label}</PageLink> : <span>{item.label}</span>}
      </li>)}</ol>
    </nav>}
    {back && <div className="ui-page-header__back"><PageLink href={back.href}>{back.label}</PageLink></div>}
    <div className="ui-page-header__main">
      <div className="ui-page-header__context" aria-busy={loading || undefined}>
        <div className="ui-page-header__title-row">
          {icon && <span aria-hidden="true">{icon}</span>}
          <h1 tabIndex={-1} className={loading ? 'ui-page-header__skeleton' : undefined}>
            {loading ? 'Loading page…' : title}
          </h1>
          {!loading && status}
        </div>
        {!loading && subtitle !== undefined && <div className="ui-page-header__subtitle">{subtitle}</div>}
      </div>
      {!!actions?.length && <div className="ui-structure-actions">
        {actions.map((action) => <FeedbackAction key={action.id} action={action} />)}
      </div>}
    </div>
  </header>;
});
